import {
  Alert,
  Box,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import {
  objectToCSVByKey,
  useLocalization,
  useUserAccess,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

function ProfileCompleteness({ profileWarnings, serviceProviderId, store }) {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const theme = useTheme();
  const userAccess = useUserAccess();
  const warnings = [];

  const isSAOrAdmin = userAccess.isSA() || userAccess.isAdmin();

  if (profileWarnings?.hasSkills === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_SKILLS_AT_LEAST_ONE_INDUSTRY_MUST_BE_CONFIGURED',
        defaultMessage:
          'Skills: At least one Industry must be configured to receive any jobs.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}/skills`);
      },
    });
  }
  if (profileWarnings?.hasNotificationEmail === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_SETUP_CUSTOMER_COMMUNICATION_TO_BE_SET_EMAIL',
        defaultMessage:
          'Setup: Dispatch Notifications Email address must be set.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}/setup`);
      },
    });
  }
  if (profileWarnings?.hasInsightCallDefault === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_SETUP_CUSTOMER_COMMUNICATION_TO_BE_SET_VOICE',
        defaultMessage:
          'Setup: Customer Communication default phone number needs to be set for Voice.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}/setup`);
      },
    });
  }
  if (profileWarnings?.hasInsightSmsDefault === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_SETUP_CUSTOMER_COMMUNICATION_TO_BE_SET_SMS',
        defaultMessage:
          'Setup: Customer Communication default phone number needs to be set for SMS Opt-in.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}/setup`);
      },
    });
  }
  if (profileWarnings?.hasDispatchMethod === false) {
    warnings.push({
      label: formatMessage(
        {
          id: 'PROFILE_COMPLETENESS_DISPATCH_METHOD_SHOULD_TO_BE_SET',
          defaultMessage:
            'System Settings (Dispatch): Dispatch method should to be set. {contactAdminMessage}',
        },
        {
          contactAdminMessage: !isSAOrAdmin
            ? formatMessage({
                id: 'CONTACT_YOUR_SERVICE_ADMINISTRATOR_TO_UPDATE',
                defaultMessage: 'Contact your Service Administrator to update.',
              })
            : '',
        },
      ),
      ...(isSAOrAdmin && {
        onClick: () => {
          navigate(
            `/serviceproviders/edit/${serviceProviderId}/systemsettings/dispatch`,
          );
        },
      }),
    });
  }

  if (profileWarnings?.isDispatchMethodInactive === true) {
    warnings.push({
      label: formatMessage(
        {
          id: 'PROFILE_COMPLETENESS_DISPATCH_METHOD_IS_INACTIVE',
          defaultMessage:
            'System Settings (Dispatch): Dispatch method is inactive. {contactAdminMessage}',
        },
        {
          contactAdminMessage: !isSAOrAdmin
            ? formatMessage({
                id: 'CONTACT_YOUR_SERVICE_ADMINISTRATOR_TO_UPDATE',
                defaultMessage: 'Contact your Service Administrator to update.',
              })
            : '',
        },
      ),
      ...(isSAOrAdmin && {
        onClick: () => {
          navigate(
            `/serviceproviders/edit/${serviceProviderId}/systemsettings/dispatch`,
          );
        },
      }),
    });
  }

  if (profileWarnings?.hasServiceTravelDistance === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_SET_DEFAULT_MESSAGE',
        defaultMessage:
          'Company Info: Default Service Travel Distance should to be set.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}`);
      },
    });
  }

  if (profileWarnings?.hasRateCard === false) {
    warnings.push({
      label: formatMessage(
        {
          id: 'PROFILE_COMPLETENESS_CLAIMS_RATE_CARD_SHOULD_BE_SET',
          defaultMessage:
            'System Settings (Claims): Rate card should be set. {contactAdminMessage}',
        },
        {
          contactAdminMessage: !isSAOrAdmin
            ? formatMessage({
                id: 'CONTACT_YOUR_SERVICE_ADMINISTRATOR_TO_UPDATE',
                defaultMessage: 'Contact your Service Administrator to update.',
              })
            : '',
        },
      ),
      ...(isSAOrAdmin && {
        onClick: () => {
          navigate(
            `/serviceproviders/edit/${serviceProviderId}/systemsettings/claims`,
          );
        },
      }),
    });
  }

  if (
    Array.isArray(profileWarnings?.hasInvalidLocationAddress) &&
    profileWarnings?.hasInvalidLocationAddress?.length > 0
  ) {
    warnings.push({
      label: formatMessage(
        {
          id: 'PROFILE_COMPLETENESS_LOCATIONS_REQUIRE_REVIEW',
          defaultMessage:
            'Locations: {count} location{plural} require{review} review for receiving jobs',
        },
        {
          count: profileWarnings?.hasInvalidLocationAddress?.length,
          plural:
            profileWarnings?.hasInvalidLocationAddress?.length > 1 ? 's' : '',
          review:
            profileWarnings?.hasInvalidLocationAddress?.length === 1 ? '' : 's',
        },
      ),
      onClick: () => {
        const locationNumberString = objectToCSVByKey(
          profileWarnings?.hasInvalidLocationAddress,
          'locationNumber',
        );
        const filters = [
          {
            field: 'locationNumber',
            operator: 'in',
            value: locationNumberString,
          },
        ];
        // ServiceProviderLocationList is store name from locations list page
        store.domain.list.set('ServiceProviderLocationList', { filters });
        navigate(`/serviceproviders/edit/${serviceProviderId}/locations`);
      },
    });
  }

  if (warnings?.length === 0) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      sx={{
        mb: 1,
        mt: 1,
        border: '1px solid',
        borderColor: theme.palette.warning.light,
        pb: 0,
      }}>
      <Box textAlign="left">
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
            {formatMessage({
              id: 'PROFILE_STATUS',
              defaultMessage: 'Profile Status',
            })}
          </Typography>
        </Box>
        <List sx={{ listStyleType: 'disc', pl: 2, pt: 0.1 }}>
          {warnings?.map(item => {
            return (
              <ListItem
                key={item?.label}
                sx={{
                  display: 'list-item',
                  pl: 0,
                  m: 0,
                  py: 0,
                  '&:hover .MuiListItemText-root': {
                    ...(item.onClick && { textDecoration: 'underline' }),
                    ...(item.onClick && { cursor: 'pointer' }),
                  },
                }}>
                <ListItemText
                  {...(item.onClick ? { onClick: item.onClick } : {})}
                  sx={{ fontSize: '0.9em' }}>
                  {item?.label}
                </ListItemText>
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box>
        <FormHelperText>
          <FormattedMessage
            id="PROFILE_COMPLETENESS_HELP_TEXT"
            defaultMessage="*Click any error to navigate to the section and make corrections."
          />
        </FormHelperText>
      </Box>
    </Alert>
  );
}

export default ProfileCompleteness;
